import React, { useEffect, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { NavBar } from '@library/menu-page'
import { queueTrackingEvent } from '@library/tracking-integration'

import {
  useCollectionShortTitle,
  useCurrentCollectionId,
  useSelectCollection,
} from 'hooks/menu-collections'
import Loading from 'routes/Menu/Loading'
import { BrowseScreen } from 'routes/Menu/MenuExperience/BrowseScreen'
import type {
  SelectBrowseEvent,
  SelectCollectionEvent,
} from 'routes/Menu/MenuExperience/MenuPage/types'
import { JustForYouTutorial } from 'routes/Menu/components/JustForYouTutorial'
import { MenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/MenuPreferencesTutorial'
import { SubHeaderContainer } from 'routes/Menu/components/SubHeader'
import { CollectionId, CollectionSlug } from 'routes/Menu/domains/collections'
import { useMenuPreferences } from 'routes/Menu/domains/menu'
import { isMenuLoading } from 'routes/Menu/selectors/menu'
import { getBasketIsFlexOrder, getBasketMenuUuid } from 'selectors/basket'
import { getUserOrdersWithRecipes } from 'selectors/user'

import { CollectionScreen } from '../CollectionScreen/CollectionScreen'
import { useGlobalBootstrap } from './useGlobalBootstrap'
import { useMenuBootstrap } from './useMenuBootstrap'

type MenuPageProps = {
  params: {
    orderId?: string
  }
  location: {
    query: {
      collection?: string
      reload?: boolean
    }
  }
}

const ContentContainer = withCitrusTheme(Box, (theme) => ({
  margin: '1rem auto 0',
  padding: '0',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    padding: '0 30px', // We need custom padding to align with the header
  },
}))

export const MenuPage = ({ params, location }: MenuPageProps) => {
  const { query } = location
  const hasMountedRef = useRef(false)

  const showLoading = useSelector(isMenuLoading)
  const { data: menuPreferences, isLoading: menuPreferencesIsLoading } = useMenuPreferences()
  const isFlexOrder = useSelector(getBasketIsFlexOrder)
  const ordersWithRecipes = useSelector(getUserOrdersWithRecipes)
  const showMenuPreferencesTutorial = ordersWithRecipes.size > 1

  const currentCollectionId = useCurrentCollectionId()
  const selectCollection = useSelectCollection()
  const collectionShortTitle = useCollectionShortTitle(CollectionId.Recommendations)
  const menuUuid = useSelector(getBasketMenuUuid)

  useMenuBootstrap({ query, params })

  useGlobalBootstrap()

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [currentCollectionId])

  const links = [
    {
      text: collectionShortTitle,
      slug: CollectionSlug.Recommendations,
      selected: currentCollectionId === CollectionId.Recommendations,
      onClick: () => {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: CollectionId.Recommendations,
            collection_slug: CollectionSlug.Recommendations,
            navigation_method: 'navigation_bar',
          },
        })
        selectCollection(CollectionId.Recommendations)
      },
    },
    {
      text: 'Browse',
      slug: CollectionId.Browse,
      selected: currentCollectionId === CollectionId.Browse,
      onClick: () => {
        queueTrackingEvent<SelectBrowseEvent>({
          action: 'select_browse',
          properties: {
            menu_uuid: menuUuid,
          },
        })
        selectCollection(CollectionId.Browse)
      },
    },
    {
      text: 'All Recipes',
      slug: CollectionSlug.AllRecipes,
      selected: currentCollectionId === CollectionId.AllRecipes,
      onClick: () => {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: CollectionId.AllRecipes,
            collection_slug: CollectionSlug.AllRecipes,
            navigation_method: 'navigation_bar',
          },
        })
        selectCollection(CollectionId.AllRecipes)
      },
    },
  ]

  const isNavBarVisible = links.some((link) => link.selected)

  if (showLoading || menuPreferencesIsLoading) {
    return (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
  }

  return (
    <Box data-testid="menuPage">
      <SubHeaderContainer />
      {!showLoading && <JustForYouTutorial />}
      {!showLoading && showMenuPreferencesTutorial && <MenuPreferencesTutorial />}
      <Box bg={Color.White} top="0" zIndex={ZIndex.Sticky} style={{ position: 'sticky' }}>
        {isNavBarVisible && <NavBar links={links} />}
      </Box>
      <ContentContainer maxWidth="1600px">
        {currentCollectionId === CollectionId.Browse ? (
          <BrowseScreen />
        ) : (
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={4}
            paddingBottom={4}
          >
            <CollectionScreen menuPreferences={menuPreferences} isFlexOrder={isFlexOrder} />
          </Box>
        )}
      </ContentContainer>
    </Box>
  )
}
